import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Renderer2,
  Output,
  EventEmitter,
  Inject,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ChangeDetector } from '../../change-detector';

let id = 1;

const MIN_SCROLL_SIZE = 35;
const ARROW_INCREMENT = 10;

@Component({
  selector: 'wfm-scrollbar',
  templateUrl: './scrollbar.component.html',
  styleUrls: ['./scrollbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScrollbarComponent extends ChangeDetector implements AfterViewInit, OnChanges {
  private $orientation = 'vertical';

  @Input() set orientation(orientation: string) {
    orientation = orientation.toLowerCase();
    if (['vertical', 'horizontal'].find(o => o === orientation)) {
      this.$orientation = orientation;
    } else {
      this.$orientation = 'vertical';
    }
  }

  get orientation(): string {
    return this.$orientation;
  }

  get style(): any {
    if (this.orientation === 'vertical') {
      if (this.reverse) {
        return {
          height: `${this.handlerSize}px`,
          bottom: `${this.handlerPosition}px`
        };
      }

      return {
        height: `${this.handlerSize}px`,
        top: `${this.handlerPosition}px`
      };
    }

    if (this.reverse) {
      return {
        width: `${this.handlerSize}px`,
        right: `${this.handlerPosition}px`
      };
    }

    return {
      width: `${this.handlerSize}px`,
      left: `${this.handlerPosition}px`
    };
  }

  @Input() offsetSize = 0;
  @Input() scrollSize = 0;
  @Input() small = false;
  @Input() hidden = false;
  @Input() reverse = false;

  /**
   * Default 10 pixels of the scroll area
   *
   * if defined, it will take it related to the scrollSize input
   */
  @Input() arrowIncrement = 0;
  private $arrowIncrement = ARROW_INCREMENT;

  private $position = 0;
  @Input() set position(position: number) {
    if (position !== this.$position) {
      this.$position = position;
      this.updateHandlerPosition();
    }
  }
  @Output() positionChange = new EventEmitter<number>();

  @Output() positionChangeStarted = new EventEmitter<number>();
  @Output() positionChangeEnded = new EventEmitter<number>();

  public isChanging = false;

  id: string;
  private railsSize = 0;
  private handlerSize = 0;
  private handlerPosition = 0;
  private lastPosition = 0;
  private scrollTimeout;
  private mouseMoveEvent: () => void;
  private mouseUpEvent: () => void;
  private mouseUpOnRailsEvent: () => void;
  private keydownEvent: () => void;

  private get scrollRange(): number {
    return this.scrollSize - this.offsetSize;
  }

  private get railsRange(): number {
    return this.railsSize - this.handlerSize;
  }

  private get rtlSignal(): number {
    if (this.reverse) return -1;
    return 1;
  }

  @ViewChild('rails') rails: ElementRef;

  constructor(
    @Inject(DOCUMENT) private document,
    cd: ChangeDetectorRef,
    private renderer: Renderer2,
    public element: ElementRef
  ) {
    super(document.defaultView, cd);
    this.id = `wfm-scroll-${id++}`;
  }

  detectChanges() {
    this.$detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.offsetSize && changes.offsetSize.currentValue !== changes.offsetSize.previousValue) ||
      (changes.scrollSize && changes.scrollSize.currentValue !== changes.scrollSize.previousValue) ||
      (changes.arrowIncrement && changes.arrowIncrement.currentValue !== changes.arrowIncrement.previousValue)
    ) {
      this.calculateHandlerSize();
      this.updateHandlerPosition();
    }
  }

  ngAfterViewInit(): void {
    this.calculateHandlerSize();
  }

  resized(): void {
    this.calculateHandlerSize();
  }

  mouseMoveStart(event: MouseEvent) {
    this.lastPosition = this.getEventPosition(event);
    this.positionChangeStarted.emit(this.$position);
    this.isChanging = true;
    this.mouseMoveEvent = this.renderer.listen('document', 'mousemove', event => this.scroll(event));
    this.mouseUpEvent = this.renderer.listen('document', 'mouseup', () => this.mouseMoveEnd());
    this.focus();
  }

  private mouseMoveEnd() {
    this.lastPosition = 0;
    this.positionChangeEnded.emit(this.$position);
    this.isChanging = false;
    this.mouseMoveEvent && this.mouseMoveEvent();
    this.mouseUpEvent && this.mouseUpEvent();

    this.$detectChanges();
  }

  private scroll(event: MouseEvent) {
    const currentPosition = this.getEventPosition(event);
    const change = currentPosition - this.lastPosition;
    this.handlerPosition += change;

    this.adjustHandlerLimits();

    this.lastPosition = currentPosition;
    this.updatePosition();

    this.$detectChanges();
  }

  private adjustHandlerLimits() {
    if (this.handlerPosition < 0) {
      this.handlerPosition = 0;
    }

    if (this.handlerPosition + this.handlerSize > this.railsSize) {
      this.handlerPosition = this.railsSize - this.handlerSize;
    }
  }

  private updateHandlerPositionTimer = 0;
  private updateHandlerPosition() {
    if (this.updateHandlerPositionTimer) return;

    this.updateHandlerPositionTimer = this.window.setTimeout(() => {
      this.handlerPosition = Math.floor((this.$position / this.scrollRange) * this.railsRange) || 0;
      this.adjustHandlerLimits();
      this.updateHandlerPositionTimer = 0;

      this.$detectChanges();
    }, 10);
  }

  private updatePosition() {
    this.$position = Math.floor((this.handlerPosition / this.railsRange) * this.scrollRange) || 0;
    this.positionChange.emit(this.$position);
  }

  mouseDownOnRails(event: MouseEvent) {
    this.mouseUpOnRails();
    const position = this.getEventPosition(event, true);

    if (
      (position < this.handlerPosition || position > this.handlerPosition + this.handlerSize) &&
      event.target === this.rails.nativeElement
    ) {
      this.scrollTo(position);
      this.positionChangeStarted.emit(this.$position);
      this.isChanging = true;
      this.mouseUpOnRailsEvent = this.renderer.listen('document', 'mouseup', () => this.mouseUpOnRails());
    }

    this.focus();
  }

  private mouseUpOnRails() {
    this.window.clearTimeout(this.scrollTimeout);
    if (this.mouseUpOnRailsEvent) {
      this.positionChangeEnded.emit(this.$position);
      this.mouseUpOnRailsEvent();
      this.isChanging = false;
    }

    this.$detectChanges();
  }

  private scrollTo(position: number) {
    const previousPosition = this.handlerPosition;

    if (position < this.handlerPosition) {
      this.handlerPosition -= this.handlerSize;
    } else if (position > this.handlerPosition + this.handlerSize) {
      this.handlerPosition += this.handlerSize;
    }

    if (previousPosition !== this.handlerPosition) {
      this.adjustHandlerLimits();
      this.updatePosition();

      this.$detectChanges();

      this.scrollTimeout = this.window.setTimeout(() => {
        this.scrollTo(position);
      }, 100);
    }
  }

  private getEventPosition(event: MouseEvent, offset = false) {
    const railsHeight = (this.rails.nativeElement as HTMLElement).offsetHeight;
    const railsWidth = (this.rails.nativeElement as HTMLElement).offsetWidth;

    if (this.orientation === 'vertical') {
      if (this.reverse) {
        return (offset && railsHeight - event.offsetY) || this.window.innerHeight - event.pageY;
      }
      return (offset && event.offsetY) || event.pageY;
    }

    if (this.reverse) {
      return (offset && railsWidth - event.offsetX) || this.window.innerWidth - event.pageX;
    }

    return (offset && event.offsetX) || event.pageX;
  }

  private calculateHandlerSizeTimer = 0;
  private calculateHandlerSize() {
    if (!this.rails || this.calculateHandlerSizeTimer) return;

    this.calculateHandlerSizeTimer = this.window.setTimeout(() => {
      this.railsSize = 0;

      if (this.orientation === 'vertical') {
        this.railsSize = (this.rails.nativeElement as HTMLElement).offsetHeight;
      } else {
        this.railsSize = (this.rails.nativeElement as HTMLElement).offsetWidth;
      }

      if (this.offsetSize >= this.scrollSize) {
        this.handlerSize = this.railsSize;
      } else {
        this.handlerSize = Math.floor((this.offsetSize / this.scrollSize) * this.railsSize);
      }

      if (this.handlerSize < MIN_SCROLL_SIZE) {
        this.handlerSize = MIN_SCROLL_SIZE;
      }

      this.calculateHandlerSizeTimer = 0;
      this.calculateArrowIncrement();

      this.$detectChanges();
    }, 10);
  }

  private calculateArrowIncrement() {
    if (!this.arrowIncrement) {
      this.$arrowIncrement = ARROW_INCREMENT;
      return;
    }

    this.$arrowIncrement = Math.floor((this.arrowIncrement / this.scrollRange) * this.railsRange) || 1;
  }

  focus() {
    if (!this.keydownEvent) {
      this.keydownEvent = this.renderer.listen('document', 'keydown', event => this.keydown(event));
    }
  }

  blur() {
    this.keydownEvent && this.keydownEvent();
    this.keydownEvent = undefined;
  }

  private keydown(event: KeyboardEvent) {
    const methodName = `${event.key}KeyPressed`;
    this[methodName] && this[methodName]();
  }

  private keyEventPosition(position: number) {
    this.handlerPosition = position;
    this.adjustHandlerLimits();
    this.updatePosition();
    this.positionChangeEnded.emit(this.$position);
    this.$detectChanges();
  }

  HomeKeyPressed() {
    this.keyEventPosition(0);
  }

  EndKeyPressed() {
    this.keyEventPosition(this.railsSize);
  }

  PageUpKeyPressed() {
    if (this.orientation === 'vertical') {
      this.keyEventPosition(this.handlerPosition - this.handlerSize);
    }
  }

  PageDownKeyPressed() {
    if (this.orientation === 'vertical') {
      this.keyEventPosition(this.handlerPosition + this.handlerSize);
    }
  }

  ArrowUpKeyPressed() {
    this.keyEventPosition(this.handlerPosition - this.$arrowIncrement);
  }

  ArrowDownKeyPressed() {
    this.keyEventPosition(this.handlerPosition + this.$arrowIncrement);
  }

  ArrowLeftKeyPressed() {
    if (this.orientation === 'horizontal') {
      this.keyEventPosition(this.handlerPosition - this.$arrowIncrement * this.rtlSignal);
    } else {
      this.keyEventPosition(this.handlerPosition + this.$arrowIncrement);
    }
  }

  ArrowRightKeyPressed() {
    if (this.orientation === 'horizontal') {
      this.keyEventPosition(this.handlerPosition + this.$arrowIncrement * this.rtlSignal);
    } else {
      this.keyEventPosition(this.handlerPosition - this.$arrowIncrement);
    }
  }
}
